import { lazy, Suspense } from "react";
import SiteHeader from "@/components/SiteHeader";
import HeroSection from "@/components/HeroSection";
import SEO, { SITE_URL, SITE_NAME } from "@/components/SEO";

// Below-the-fold sections are code-split so the initial JS payload only ships
// the hero + header. Each chunk loads after first paint.
const BreakingNewsTicker = lazy(() => import("@/components/BreakingNewsTicker"));
const TrendingTopics = lazy(() => import("@/components/TrendingTopics"));
const FeaturedArticles = lazy(() => import("@/components/FeaturedArticles"));
const MoreArticlesSection = lazy(() => import("@/components/MoreArticlesSection"));
const SiteFooter = lazy(() => import("@/components/SiteFooter"));

const Index = () => {
  const organizationLd = {
    "@context": "https://schema.org",
    "@type": "NewsMediaOrganization",
    name: SITE_NAME,
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: `${SITE_URL}/favicon.png`,
    },
    sameAs: [
      "https://facebook.com/profile.php?id=100077301390535",
    ],
    address: {
      "@type": "PostalAddress",
      streetAddress: "Dubai Media City, Building 7, Office 301",
      addressLocality: "Dubai",
      addressCountry: "AE",
    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Editorial",
      email: "editorial@periodicaltoday.com",
      areaServed: "Worldwide",
      availableLanguage: ["English"],
    },
  };

  const websiteLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: SITE_URL,
    description:
      "Periodical Today delivers reliable global news on politics, business, technology, health, entertainment, sports, and current affairs.",
    publisher: { "@type": "Organization", name: SITE_NAME },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title="Periodical Today | Global News, Politics & Current Affairs"
        description="Periodical Today delivers reliable global news on politics, business, technology, health, entertainment, sports, and current affairs in one trusted platform."
        path="/"
        keywords={["news", "world news", "politics", "business", "technology", "health", "entertainment", "sports", "UAE news", "Dubai news"]}
        jsonLd={[organizationLd, websiteLd]}
      />
      <SiteHeader />
      <Suspense fallback={<div className="h-9 bg-secondary" aria-hidden />}><BreakingNewsTicker /></Suspense>
      <main className="flex-1">
        <HeroSection />
        <Suspense fallback={<div className="h-10 border-b border-border bg-background" aria-hidden />}><TrendingTopics /></Suspense>
        <Suspense fallback={<div className="min-h-[600px]" aria-hidden />}><FeaturedArticles /></Suspense>
        <Suspense fallback={<div className="min-h-[400px]" aria-hidden />}><MoreArticlesSection /></Suspense>
      </main>
      <Suspense fallback={<div className="h-[300px]" aria-hidden />}><SiteFooter /></Suspense>
    </div>
  );
};

export default Index;
